/**
 * Public rendering of a tenant-owned page-builder component — BYOD Phase 4/8.
 *
 * Studio stores these as an envelope (`{component, version, props}`) because
 * it cannot import the tenant's React code. This is where the name is matched
 * back to the tenant's registry and the component actually renders on the
 * published page.
 *
 * Without this, a page authored in the tenant editor would look right in the
 * editor and render NOTHING once published — the editor renders through the
 * tenant's Puck config, the public page renders through renderPageSection, and
 * only the former knew about tenant components.
 *
 * The tenant ROOT is deliberately not applied here. It owns page background,
 * header and footer, and the storefront's own Layout already provides that
 * chrome on `/p/<slug>` — wrapping again would double the header. The root is
 * the editor canvas's shell; the public page keeps the site's.
 */
import { registry as tenantRegistry } from '@crosure/tenant/pageBuilder'
import type { TenantComponentRegistry } from '@crosure/tenant-runtime'
import React from 'react'

/**
 * Asserted once: webpack aliases this module to a null stub for tenants
 * without a page-builder registry (see next.config.js), so TypeScript cannot
 * know the shape. `null` stays in the type because it is a real runtime state.
 */
const registry = tenantRegistry as TenantComponentRegistry | null

/** Product data resolved from the stored codes, injected at render time. */
export interface ResolvedTenantProduct {
	code: string
	name?: string
	summary?: string
	premiumFrom?: string
	href?: string
}

interface Props {
	config: Record<string, unknown>
	products?: ResolvedTenantProduct[]
}

export function TenantComponentSection({ config, products = [] }: Props) {
	const name = typeof config.component === 'string' ? config.component : ''
	const props = (config.props ?? {}) as Record<string, unknown>

	const definition = name ? registry?.components?.[name] : undefined
	if (!definition) {
		// A published page naming a component this build does not ship. Studio
		// blocks publishing in that state, so reaching here means the deployed
		// package is behind the manifest Studio validated against — usually a
		// tenant_version pin that was not moved.
		//
		// Reported from getServerSideProps, not here: a console.error inside a
		// component's render was NOT surfaced by `next start`, so a warning
		// placed here is one nobody sees. See lib/pageBuilder/unknownComponents.
		//
		// Render nothing rather than an error: a visitor should see a page with
		// one section missing, not a broken page.
		return null
	}

	const Component = definition.render as React.ComponentType<
		Record<string, unknown>
	>

	// `products` is injected, never authored: a component that wants resolved
	// product data declares `productCodes` and receives this alongside it.
	return <Component {...props} products={products} />
}

export default TenantComponentSection
